import { useRef } from "react";
import { createFileRoute, isRedirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { Card, CardContent, CardHeader, CardTitle } from "@beweegbaas/ui";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  getSignupStep1DataFn,
  submitSignupStep1Fn,
} from "#/modules/generic.signup/signup.functions";
import { signupStep1Schema } from "#/modules/generic.signup/schema.signup.step-1";
import { useAppForm } from "#/modules/util.forms/form";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { op } from "#/modules/util.instrumentation/op";

export const Route = createFileRoute("/$locale/{-$schoolyear}/signup/step/1")({
  loader: () => getSignupStep1DataFn(),
  component: SignupStep1Page,
});

function SignupStep1Page() {
  const defaults = Route.useLoaderData();
  const navigate = useNavigate();
  const submitStep1 = useServerFn(submitSignupStep1Fn);

  // Real top-of-funnel signal: fired once on the first keystroke, so bounces and
  // bots that merely load the page don't count as "started signup".
  const signupStartedTracked = useRef(false);
  const trackSignupStarted = () => {
    if (signupStartedTracked.current) return;
    signupStartedTracked.current = true;
    void op.track("signup_started");
  };

  const form = useAppForm({
    defaultValues: {
      name: defaults.name,
      surname: defaults.surname,
      email: defaults.email,
      // Never prefilled from server data — the plaintext password isn't sent to the
      // client, so on a stale-session revisit the user retypes it.
      password: "",
    },
    validators: { onSubmit: signupStep1Schema },
    onSubmit: async ({ value }) => {
      try {
        const res = await submitStep1({ data: value });
        if (res) {
          form.setErrorMap({ onSubmit: { form: res.formError, fields: {} } });
          return;
        }
        void op.track("signup_step_completed", { step: 1 });
        void navigate({
          from: Route.fullPath,
          to: "/$locale/{-$schoolyear}/signup/step/2",
          params: (prev) => prev,
        });
      } catch (error) {
        if (isRedirect(error)) throw error;
        captureUnexpectedError(error, "signup-step-1");
        form.setErrorMap({
          onSubmit: {
            form: m["common.errors.unexpected_try_again"](),
            fields: {},
          },
        });
      }
    },
  });

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["signup.step1.title"]()}</CardTitle>
          <p className="text-muted-foreground text-sm">{m["signup.step1.subtitle"]()}</p>
        </CardHeader>
        <CardContent onInputCapture={trackSignupStarted}>
          <form.AppForm>
            <form.FormRoot className="flex flex-col gap-4">
              <form.AppField
                name="name"
                children={(field) => <field.TextField label={m["common.fields.first_name"]()} />}
              />
              <form.AppField
                name="surname"
                children={(field) => <field.TextField label={m["common.fields.last_name"]()} />}
              />
              <form.AppField
                name="email"
                children={(field) => <field.TextField label={m["common.fields.email"]()} />}
              />
              <form.AppField
                name="password"
                children={(field) => (
                  <field.PasswordTextField label={m["common.fields.password"]()} showRequirements />
                )}
              />

              <form.SubmitButton className="mt-6 w-full">
                {m["common.actions.next"]()}
              </form.SubmitButton>
            </form.FormRoot>
          </form.AppForm>
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
